/* =====================================================================
 * EmailModal — Gmail / Outlook 메일 선택 모달
 * ===================================================================== */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import styled, { css } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import {
  Mail,
  X,
  Paperclip,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  Loader,
  Download,
  ArrowLeft,
  Inbox,
  FileIcon,
} from 'lucide-react';

import { Modal } from '@auw/workspace-ui';
import { Button } from '@auw/workspace-ui';
import { EmptyState } from '@auw/workspace-ui';
import { Checkbox } from '@auw/workspace-ui';
import { Input } from '@auw/workspace-ui';
import { SkeletonLine } from '@auw/workspace-ui';
import { Tabs as BaseTabs, type TabItem } from '@auw/workspace-ui';
import { RingSpinner } from '@auw/workspace-ui';
import {
  listPlugins,
  listGmailMessages,
  listOutlookMessages,
  getGmailThread,
  getOutlookConversation,
  getGmailMessage,
  getOutlookMessage,
  listGmailLabels,
  listOutlookFolders,
  getMailAttachmentDownloadUrl,
} from '@auw/api-client';
import type {
  MailProvider,
  EmailItem,
  EmailAttachmentMeta,
  PluginInfo,
  GmailLabel,
  OutlookFolder,
} from '@auw/api-client';
import type { FileAttachment } from '@auw/workspace-utils';
import { useChatStore } from '@auw/stores';
import { useWorkspaceNavigate } from '@auw/workspace-features';
import { queryKeys } from '@auw/workspace-utils';
import {
  CHAT_ATTACHMENT_MAX_COUNT,
  buildAttachmentCountLimitToast,
  buildAttachmentSizeLimitToast,
  getRemainingAttachmentSlots,
  isAttachmentSizeAllowed,
  MODAL_GC_TIME,
  MODAL_STALE_TIME,
} from './attachmentConstraints';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

interface EmailModalProps {
  open: boolean;
  onClose: () => void;
  initialTab: MailProvider;
  onSelect: (files: FileAttachment[]) => void;
  existingAttachmentCount?: number;
  onTabChange?: (tab: MailProvider) => void;
  showUploadActions?: boolean;
  modalDraggable?: boolean;
  modalPinnable?: boolean;
  modalDefaultPinned?: boolean;
  modalInitialOffsetX?: number;
  modalInitialOffsetY?: number;
  modalOpacityAdjustable?: boolean;
  modalDefaultOpacity?: number;
  modalDefaultWidth?: number;
  modalDefaultHeight?: number;
  modalMinWidth?: number;
  modalCloseOnBackdrop?: boolean;
  modalDimBackdrop?: boolean;
  modalPassthroughBackdrop?: boolean;
  modalCenterPaddingLeft?: number;
  modalCenterPaddingRight?: number;
  mountMode?: 'viewport' | 'parent';
  hostBounds?: { width: number; height: number };
  modalOnSizeChange?: (size: { w: number; h: number }) => void;
  modalOnOffsetChange?: (offset: { x: number; y: number }) => void;
  modalOnPinnedChange?: (pinned: boolean) => void;
  modalOnOpacityChange?: (opacity: number) => void;
  modalLayerZIndex?: number;
  modalOnActivate?: () => void;
}

interface SelectedAttachment {
  key: string;
  mailSource: MailProvider;
  messageId: string;
  attachmentId: string;
  filename: string;
  mimeType: string;
  size: number;
}

interface SelectedThread {
  key: string;
  mailSource: MailProvider;
  threadId: string;
  subject: string;
  messageCount: number;
}

interface SelectedMessage {
  key: string;
  mailSource: MailProvider;
  messageId: string;
  subject: string;
}

type SelectedItem = SelectedAttachment | SelectedThread | SelectedMessage;

function isSelectedAttachment(item: SelectedItem): item is SelectedAttachment {
  return 'attachmentId' in item;
}

function isSelectedThread(item: SelectedItem): item is SelectedThread {
  return 'threadId' in item && !('attachmentId' in item);
}

const OUTLOOK_WELL_KNOWN_FOLDER_I18N: Record<string, string> = {
  inbox: 'email.inbox',
  sentitems: 'email.sent',
  drafts: 'email.drafts',
};

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function uuid(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function formatDate(dateStr?: string, t?: (key: string, opts?: Record<string, unknown>) => string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
    }
    if (diffDays === 1) return t?.('email.yesterday') ?? '';
    if (diffDays < 7) return t?.('email.daysAgo', { count: diffDays }) ?? `${diffDays}d`;
    return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function formatFileSize(bytes?: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extractSenderName(sender: string): string {
  const match = sender.match(/^(.+?)\s*<.*>$/);
  return match ? match[1].trim().replace(/^"|"$/g, '') : sender;
}

function extractSenderEmail(sender: string): string {
  const match = sender.match(/<(.+?)>/);
  return match ? match[1] : sender;
}

function normalizeText(text: string): string {
  return text
    .replace(/\u00a0/g, ' ')
    .replace(/\r/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function removeLikelyCssLines(text: string): string {
  const lines = text.split('\n');
  const filtered = lines.filter((line) => {
    const trimmed = line.trim();
    if (!trimmed) return true;
    if (trimmed.startsWith('@media')) return false;
    if (/^data-styled\./.test(trimmed)) return false;
    if (/^\.[A-Za-z0-9_-]+\s*,?$/.test(trimmed)) return false;
    if (/^\.[A-Za-z0-9_-]+\s*\{?$/.test(trimmed)) return false;
    if (/^[{}]+$/.test(trimmed)) return false;
    if (
      /^[\w-]+\s*:\s*[^;]+;?\s*$/.test(trimmed)
      && (
        trimmed.includes('!important')
        || /^(background|color|display|padding|height|width|margin|font|line-height)\b/.test(trimmed)
      )
    ) {
      return false;
    }
    return true;
  });
  return normalizeText(filtered.join('\n'));
}

function extractCleanBodyText(body?: string): string {
  if (!body) return '';
  const raw = body.trim();
  if (!raw) return '';

  if (!raw.includes('<')) {
    return removeLikelyCssLines(raw);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(raw, 'text/html');
  doc.querySelectorAll('style,script,noscript,template,svg,head,meta,link,title').forEach((node) => {
    node.remove();
  });
  const text = doc.body?.innerText || doc.body?.textContent || '';
  return removeLikelyCssLines(text);
}

type BodyRenderPayload =
  | { kind: 'empty' }
  | { kind: 'html'; srcDoc: string }
  | {
    kind: 'text';
    format: 'markdown' | 'plain';
    main: string;
    forwarded: string | null;
    signature: string | null;
  };

const FORWARD_MARKERS: RegExp[] = [
  /^\s*-{2,}\s*Forwarded message\s*-{2,}\s*$/im,
  /^\s*-{2,}\s*Original Message\s*-{2,}\s*$/im,
  /^\s*Begin forwarded message:\s*$/im,
  /^\s*전달된 메시지\s*$/im,
  /^\s*-{2,}\s*전달된 메시지\s*-{2,}\s*$/im,
];

const FORWARD_HEADER_LINE = /^\s*(From|보낸 사람|발신)\s*:\s.+$/im;

const SIGNATURE_MARKERS: RegExp[] = [
  /\n--\s*\n/,
  /\n__+\s*\n/,
  /\nSent from my (?:iPhone|iPad|Android).*/i,
  /\nGet Outlook for (?:iOS|Android).*/i,
];

const MARKDOWN_REMARK_PLUGINS = [remarkGfm];

const MARKDOWN_LINK_RE = /\[[^\]\n]+]\((?:https?:\/\/|mailto:)[^)]+\)/i;
const MARKDOWN_AUTOLINK_RE = /(^|\s)<(?:https?:\/\/|mailto:)[^>\s]+>/i;
const MARKDOWN_HEADING_RE = /^#{1,6}\s+\S/m;
const MARKDOWN_LIST_RE = /^\s{0,3}(?:[-*+]|\d+\.)\s+\S/m;
const MARKDOWN_QUOTE_RE = /^\s{0,3}>\s+\S/m;

function normalizeBodyTextForRender(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/\u00a0/g, ' ')
    .replace(/\n{4,}/g, '\n\n\n')
    .trim();
}

function looksLikeHtml(content: string): boolean {
  const source = content.trim();
  if (!source) return false;
  const hasKnownOpeningTag = /<(?:html|body|div|p|br|span|table|tr|td|th|ul|ol|li|blockquote|pre|a|img|style|head|section|article)(?:\s|>|\/)/i.test(source);
  const hasClosingTag = /<\/[a-z][a-z0-9:-]*\s*>/i.test(source);
  return /<!doctype html/i.test(source) || hasKnownOpeningTag || hasClosingTag;
}

function buildEmailHtmlSrcDoc(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  doc.querySelectorAll('script,noscript').forEach((node) => {
    node.remove();
  });
  doc.querySelectorAll('*').forEach((el) => {
    const attrs = Array.from(el.attributes);
    attrs.forEach((attr) => {
      const attrName = attr.name.toLowerCase();
      const attrValue = attr.value.trim().toLowerCase();
      if (attrName.startsWith('on')) {
        el.removeAttribute(attr.name);
        return;
      }
      if ((attrName === 'href' || attrName === 'src') && attrValue.startsWith('javascript:')) {
        el.removeAttribute(attr.name);
      }
    });
  });

  const hasDocumentShell = /<!doctype html/i.test(html) || /<html[\s>]/i.test(html);
  if (hasDocumentShell) {
    return `<!doctype html>${doc.documentElement.outerHTML}`;
  }

  return [
    '<!doctype html>',
    '<html>',
    '<head>',
    '<meta charset="utf-8" />',
    '</head>',
    '<body>',
    doc.body?.innerHTML || html,
    '</body>',
    '</html>',
  ].join('');
}

function looksLikeMarkdown(content: string): boolean {
  const source = content.trim();
  if (!source) return false;
  return MARKDOWN_LINK_RE.test(source)
    || MARKDOWN_AUTOLINK_RE.test(source)
    || MARKDOWN_HEADING_RE.test(source)
    || MARKDOWN_LIST_RE.test(source)
    || MARKDOWN_QUOTE_RE.test(source);
}

function findFirstMatchIndex(text: string, patterns: RegExp[]): number {
  let min = -1;
  patterns.forEach((pattern) => {
    const match = pattern.exec(text);
    if (!match || typeof match.index !== 'number') return;
    if (min === -1 || match.index < min) min = match.index;
  });
  return min;
}

function findForwardStartIndex(text: string): number {
  const direct = findFirstMatchIndex(text, FORWARD_MARKERS);
  if (direct >= 0) return direct;

  const headerMatch = FORWARD_HEADER_LINE.exec(text);
  if (!headerMatch || typeof headerMatch.index !== 'number') return -1;
  if (!text.slice(0, headerMatch.index).trim()) return -1;

  const preview = text.slice(headerMatch.index, headerMatch.index + 360);
  const hasForwardContext = /(sent|date|to|subject|받는 사람|제목|수신)\s*:/i.test(preview);
  return hasForwardContext ? headerMatch.index : -1;
}

function findSignatureStartIndex(text: string): number {
  let min = -1;
  SIGNATURE_MARKERS.forEach((pattern) => {
    const match = pattern.exec(text);
    if (!match || typeof match.index !== 'number') return;
    const start = match[0].startsWith('\n') ? match.index + 1 : match.index;
    if (min === -1 || start < min) min = start;
  });
  return min;
}

function splitPlainBodySections(body: string): { main: string; forwarded: string | null; signature: string | null } {
  let working = normalizeBodyTextForRender(body);
  if (!working) {
    return { main: '', forwarded: null, signature: null };
  }

  let forwarded: string | null = null;
  const forwardStart = findForwardStartIndex(working);
  if (forwardStart >= 0) {
    forwarded = working.slice(forwardStart).trim();
    working = working.slice(0, forwardStart).trimEnd();
  }

  let signature: string | null = null;
  const signatureStart = findSignatureStartIndex(working);
  if (signatureStart >= 0) {
    signature = working.slice(signatureStart).trim();
    working = working.slice(0, signatureStart).trimEnd();
  }

  return {
    main: working,
    forwarded,
    signature,
  };
}

function buildBodyRenderPayload(body?: string, snippet?: string): BodyRenderPayload {
  const source = (body || snippet || '').trim();
  if (!source) return { kind: 'empty' };

  if (looksLikeHtml(source)) {
    return { kind: 'html', srcDoc: buildEmailHtmlSrcDoc(source) };
  }

  const sections = splitPlainBodySections(source);
  if (!sections.main && !sections.forwarded && !sections.signature) {
    return { kind: 'empty' };
  }
  const markdownProbe = [sections.main, sections.forwarded, sections.signature]
    .filter((part): part is string => !!part)
    .join('\n');

  return {
    kind: 'text',
    format: looksLikeMarkdown(markdownProbe) ? 'markdown' : 'plain',
    ...sections,
  };
}

function formatRecipients(msg: EmailItem): string {
  const recipients = [...(msg.recipients || [])];
  if (msg.cc?.length) {
    recipients.push(...msg.cc.map((cc) => `CC:${cc}`));
  }
  return recipients.length > 0 ? recipients.join(', ') : '-';
}

function formatAttachmentNames(msg: EmailItem): string {
  if (!msg.attachments || msg.attachments.length === 0) return '-';
  return msg.attachments.map((att) => att.filename).join(', ');
}

function formatMessageAsText(msg: EmailItem, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const sender = extractSenderName(msg.sender) || extractSenderEmail(msg.sender) || '-';
  const body = extractCleanBodyText(msg.body || msg.snippet) || t('email.noContent');

  return [
    `${t('email.labelSubject')}: ${msg.subject || t('email.noSubject')}`,
    `${t('email.labelSender')}: ${sender}`,
    `${t('email.labelRecipient')}: ${formatRecipients(msg)}`,
    `${t('email.labelAttachment')}: ${formatAttachmentNames(msg)}`,
    '',
    `${t('email.labelContent')}:`,
    body,
  ].join('\n');
}

function formatThreadAsText(messages: EmailItem[], t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (messages.length === 0) return '';
  return messages
    .map((msg, idx) => `[${idx + 1}]\n${formatMessageAsText(msg, t)}`)
    .join('\n\n------------------------------\n\n');
}

function isRateLimitRetryExhaustedError(
  err: unknown,
): err is Error & { code: 'RATE_LIMIT_RETRY_EXHAUSTED'; retryAfterMs?: number } {
  return !!err && typeof err === 'object' && (err as { code?: string }).code === 'RATE_LIMIT_RETRY_EXHAUSTED';
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const EmailModal: React.FC<EmailModalProps> = ({
  open,
  onClose,
  initialTab,
  onSelect,
  existingAttachmentCount = 0,
  onTabChange,
  showUploadActions = true,
  modalDraggable = false,
  modalPinnable = false,
  modalDefaultPinned = false,
  modalInitialOffsetX = 0,
  modalInitialOffsetY = 0,
  modalOpacityAdjustable = false,
  modalDefaultOpacity = 1,
  modalDefaultWidth = 860,
  modalDefaultHeight = 640,
  modalMinWidth = 480,
  modalCloseOnBackdrop = true,
  modalDimBackdrop = true,
  modalPassthroughBackdrop = false,
  modalCenterPaddingLeft = 0,
  modalCenterPaddingRight = 0,
  mountMode = 'viewport',
  hostBounds,
  modalOnSizeChange,
  modalOnOffsetChange,
  modalOnPinnedChange,
  modalOnOpacityChange,
  modalLayerZIndex,
  modalOnActivate,
}) => {
  const { t } = useTranslation('common');
  const { openPluginDetail } = useWorkspaceNavigate();
  const addToast = useChatStore((s) => s.addToast);
  const lastRateLimitToastTsRef = useRef(0);
  const listAbortRef = useRef<AbortController | null>(null);

  const [activeTab, setActiveTab] = useState<MailProvider>(initialTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const [activeLabel, setActiveLabel] = useState('INBOX');
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  const [selectedMsg, setSelectedMsg] = useState<EmailItem | null>(null);
  const [threadMessages, setThreadMessages] = useState<EmailItem[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [expandedThreadMsgs, setExpandedThreadMsgs] = useState<Set<string>>(new Set());

  const [selected, setSelected] = useState<Map<string, SelectedItem>>(new Map());

  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const layoutRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);
  const [compactLayout, setCompactLayout] = useState(false);
  const surfaceOpacity = Math.min(1, Math.max(0.35, modalDefaultOpacity));

  const {
    data: pluginsData,
    isFetching: pluginsFetching,
    refetch: refetchPlugins,
  } = useQuery<PluginInfo[]>({
    queryKey: queryKeys.plugins.list(),
    queryFn: async () => {
      const resp = await listPlugins();
      return resp.plugins;
    },
    enabled: open,
    refetchOnWindowFocus: true,
  });
  const plugins = pluginsData ?? [];
  const pluginsLoading = pluginsFetching && !pluginsData;

  const { data: gmailLabels } = useQuery<GmailLabel[]>({
    queryKey: ['gmail-labels'],
    queryFn: async () => {
      const resp = await listGmailLabels();
      return resp.labels;
    },
    enabled: open && activeTab === 'gmail' && plugins.some((p) => p.id === 'gmail' && p.connected),
  });

  const { data: outlookFolders } = useQuery<OutlookFolder[]>({
    queryKey: ['outlook-folders'],
    queryFn: async () => {
      const resp = await listOutlookFolders();
      return resp.folders;
    },
    enabled: open && activeTab === 'outlook' && plugins.some((p) => p.id === 'outlook' && p.connected),
  });

  const tabItems: TabItem[] = [
    { id: 'gmail', label: 'Gmail', icon: <TabIcon src="/logo/google_gmail.webp" alt="Gmail" /> },
    { id: 'outlook', label: 'Outlook', icon: <TabIcon src="/logo/outlook.webp" alt="Outlook" /> },
  ];

  const isConnected = (provider: MailProvider): boolean =>
    plugins.some((p) => p.id === provider && p.connected);

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    if (justOpened) {
      setActiveTab(initialTab);
      setSelected(new Map());
      setSelectedMsg(null);
      setThreadMessages([]);
      setSearchQuery('');
      setDebouncedSearchQuery('');
    }
    if (!open) {
      setShowFilterDropdown(false);
    }
    wasOpenRef.current = open;
  }, [open, initialTab]);

  useEffect(() => {
    if (!open) return;
    const el = layoutRef.current;
    if (!el) return;

    const updateCompact = () => {
      setCompactLayout(el.clientWidth <= 760);
    };

    updateCompact();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateCompact);
      return () => window.removeEventListener('resize', updateCompact);
    }

    const observer = new ResizeObserver(updateCompact);
    observer.observe(el);
    return () => observer.disconnect();
  }, [open]);

  useEffect(() => {
    if (open) void refetchPlugins();
  }, [open, refetchPlugins]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => () => {
    listAbortRef.current?.abort();
  }, []);

  const showRateLimitToast = useCallback((err: unknown) => {
    if (!isRateLimitRetryExhaustedError(err)) return;
    const now = Date.now();
    if (now - lastRateLimitToastTsRef.current < 2500) return;
    lastRateLimitToastTsRef.current = now;
    const retryAfterSec = Math.ceil((err.retryAfterMs ?? 1000) / 1000);
    addToast({
      type: 'warning',
      title: t('email.tooManyRequests'),
      description: t('email.retryAfter', { seconds: retryAfterSec }),
    });
  }, [addToast]);

  const showAttachmentCountLimitToast = useCallback((skippedCount?: number) => {
    const toast = buildAttachmentCountLimitToast(skippedCount);
    addToast({
      type: 'warning',
      title: toast.title,
      description: toast.description,
    });
  }, [addToast]);

  const showAttachmentSizeLimitToast = useCallback((attachment: Pick<EmailAttachmentMeta, 'filename' | 'size'>) => {
    const toast = buildAttachmentSizeLimitToast([{ name: attachment.filename, size: attachment.size }]);
    addToast({
      type: 'warning',
      title: toast.title,
      description: toast.description,
    });
  }, [addToast]);

  const normalizedSearchQuery = searchQuery.trim();
  const effectiveSearchQuery = normalizedSearchQuery.length >= 2 ? normalizedSearchQuery : '';

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(effectiveSearchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [effectiveSearchQuery]);

  const messagesQuery = useInfiniteQuery({
    queryKey: [
      'email-modal-messages',
      activeTab,
      activeTab === 'gmail' ? activeLabel : activeFolder,
      debouncedSearchQuery,
    ],
    enabled: open && !pluginsLoading && isConnected(activeTab),
    initialPageParam: undefined as string | undefined,
    staleTime: MODAL_STALE_TIME,
    gcTime: MODAL_GC_TIME,
    refetchOnWindowFocus: false,
    queryFn: async ({ pageParam, signal }) => {
      listAbortRef.current?.abort();
      const controller = new AbortController();
      listAbortRef.current = controller;
      const abortBySignal = () => controller.abort();
      signal?.addEventListener('abort', abortBySignal, { once: true });

      try {
        if (activeTab === 'gmail') {
          return await listGmailMessages(
            debouncedSearchQuery || undefined,
            activeLabel,
            20,
            pageParam || undefined,
            { signal: controller.signal },
          );
        }
        return await listOutlookMessages(
          debouncedSearchQuery || undefined,
          activeFolder,
          20,
          pageParam || undefined,
          { signal: controller.signal },
        );
      } finally {
        signal?.removeEventListener('abort', abortBySignal);
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextPageToken ?? undefined,
  });

  const messages = useMemo(
    () => messagesQuery.data?.pages.flatMap((p) => p.messages) ?? [],
    [messagesQuery.data],
  );
  const loading = messagesQuery.isLoading;
  const loadingMore = messagesQuery.isFetchingNextPage;
  const error = messagesQuery.error instanceof Error ? messagesQuery.error.message : null;

  useEffect(() => {
    if (!messagesQuery.error) return;
    showRateLimitToast(messagesQuery.error);
  }, [messagesQuery.error, showRateLimitToast]);

  useEffect(() => {
    if (!open || pluginsLoading) return;
    if (!isConnected(activeTab)) return;
    setSelected(new Map());
    setSelectedMsg(null);
    setThreadMessages([]);
  }, [activeTab, open, pluginsLoading, activeLabel, activeFolder, debouncedSearchQuery]);

  /* ---- Search ---- */
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  /* ---- Infinite scroll ---- */
  useEffect(() => {
    if (!sentinelRef.current || !scrollRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting
          && messagesQuery.hasNextPage
          && !messagesQuery.isFetchingNextPage
        ) {
          void messagesQuery.fetchNextPage();
        }
      },
      { root: scrollRef.current, threshold: 0.1 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [messagesQuery.hasNextPage, messagesQuery.isFetchingNextPage, messagesQuery.fetchNextPage]);

  /* ---- Select message / load thread ---- */
  const handleMsgClick = useCallback(async (msg: EmailItem) => {
    setSelectedMsg(msg);
    setThreadMessages([]);
    setLoadingThread(true);
    setExpandedThreadMsgs(new Set([msg.id]));
    try {
      if (activeTab === 'gmail' && msg.thread_id) {
        const thread = await getGmailThread(msg.thread_id);
        setThreadMessages(thread.messages);
        setExpandedThreadMsgs(new Set([thread.messages[thread.messages.length - 1]?.id]));
      } else if (activeTab === 'outlook' && msg.conversation_id) {
        const conv = await getOutlookConversation(msg.id);
        setThreadMessages(conv.messages);
        setExpandedThreadMsgs(new Set([conv.messages[conv.messages.length - 1]?.id]));
      } else {
        let detail;
        if (activeTab === 'gmail') {
          detail = await getGmailMessage(msg.id);
        } else {
          detail = await getOutlookMessage(msg.id);
        }
        setThreadMessages([detail]);
        setExpandedThreadMsgs(new Set([detail.id]));
      }
    } catch (e) {
      showRateLimitToast(e);
      setThreadMessages([{ ...msg }]);
      setExpandedThreadMsgs(new Set([msg.id]));
    } finally {
      setLoadingThread(false);
    }
  }, [activeTab, showRateLimitToast]);

  /* ---- Selection logic ---- */
  const toggleAttachment = useCallback((
    att: EmailAttachmentMeta,
    mailSource: MailProvider,
  ) => {
    const key = `att:${att.message_id}:${att.id}`;
    const isSelected = selected.has(key);

    if (!isSelected && !isAttachmentSizeAllowed(att.size)) {
      showAttachmentSizeLimitToast(att);
      return;
    }

    if (!isSelected && getRemainingAttachmentSlots(existingAttachmentCount, selected.size) === 0) {
      showAttachmentCountLimitToast();
      return;
    }

    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, {
          key,
          mailSource,
          messageId: att.message_id,
          attachmentId: att.id,
          filename: att.filename,
          mimeType: att.mime_type,
          size: att.size,
        });
      }
      return next;
    });
  }, [existingAttachmentCount, selected, showAttachmentCountLimitToast, showAttachmentSizeLimitToast]);

  const toggleThreadImport = useCallback((msg: EmailItem, provider: MailProvider) => {
    const threadId = msg.thread_id || msg.conversation_id || msg.id;
    const key = `thread:${threadId}`;
    const isSelected = selected.has(key);

    if (!isSelected && getRemainingAttachmentSlots(existingAttachmentCount, selected.size) === 0) {
      showAttachmentCountLimitToast();
      return;
    }

    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, {
          key,
          mailSource: provider,
          threadId,
          subject: msg.subject,
          messageCount: threadMessages.length,
        });
      }
      return next;
    });
  }, [existingAttachmentCount, selected, showAttachmentCountLimitToast, threadMessages]);

  const toggleMessageImport = useCallback((msg: EmailItem, provider: MailProvider) => {
    const key = `msg:${msg.id}`;
    const isSelected = selected.has(key);

    if (!isSelected && getRemainingAttachmentSlots(existingAttachmentCount, selected.size) === 0) {
      showAttachmentCountLimitToast();
      return;
    }

    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, {
          key,
          mailSource: provider,
          messageId: msg.id,
          subject: msg.subject,
        });
      }
      return next;
    });
  }, [existingAttachmentCount, selected, showAttachmentCountLimitToast]);

  /* ---- Handle attach ---- */
  const handleAttach = useCallback(() => {
    const attachments: FileAttachment[] = [];

    for (const item of selected.values()) {
      if (isSelectedAttachment(item)) {
        attachments.push({
          id: uuid(),
          name: item.filename,
          type: item.mimeType,
          size: item.size,
          mailSource: item.mailSource,
          mailMessageId: item.messageId,
          mailAttachmentId: item.attachmentId,
          remoteUrl: getMailAttachmentDownloadUrl(
            item.mailSource,
            item.messageId,
            item.attachmentId,
            item.filename,
          ),
        });
      } else if (isSelectedThread(item)) {
        const threadSource = threadMessages.length > 0
          ? threadMessages
          : (selectedMsg ? [selectedMsg] : []);
        const content = formatThreadAsText(threadSource, t);
        const blob = new File(
          [content],
          `${item.subject.slice(0, 50).replace(/[/\\:*?"<>|]/g, '_')}_스레드.txt`,
          { type: 'text/plain' },
        );
        attachments.push({
          id: uuid(),
          name: blob.name,
          type: 'text/plain',
          size: blob.size,
          file: blob,
        });
      } else {
        const msg = threadMessages.find((m) => m.id === item.messageId) || selectedMsg;
        if (msg) {
          const content = formatMessageAsText(msg, t);
          const blob = new File(
            [content],
            `${msg.subject.slice(0, 50).replace(/[/\\:*?"<>|]/g, '_')}.txt`,
            { type: 'text/plain' },
          );
          attachments.push({
            id: uuid(),
            name: blob.name,
            type: 'text/plain',
            size: blob.size,
            file: blob,
          });
        }
      }
    }

    onSelect(attachments);
    onClose();
  }, [selected, threadMessages, selectedMsg, onSelect, onClose]);

  /* ---- Go to plugin ---- */
  const handleGoToPlugin = useCallback(() => {
    const pluginId = activeTab;
    openPluginDetail(pluginId);
    onClose();
  }, [activeTab, onClose, openPluginDetail]);

  const handleHeaderRefresh = useCallback(() => {
    void refetchPlugins();
    void messagesQuery.refetch();
    if (selectedMsg) {
      void handleMsgClick(selectedMsg);
    }
  }, [refetchPlugins, messagesQuery, selectedMsg, handleMsgClick]);

  /* ---- Selection summary ---- */
  const selectionSummary = useMemo(() => {
    if (selected.size === 0) {
      return existingAttachmentCount > 0
        ? `${existingAttachmentCount}/${CHAT_ATTACHMENT_MAX_COUNT}`
        : t('email.selectItems');
    }

    let attCount = 0;
    let threadCount = 0;
    let msgCount = 0;
    for (const item of selected.values()) {
      if (isSelectedAttachment(item)) attCount++;
      else if (isSelectedThread(item)) threadCount++;
      else msgCount++;
    }
    const parts: string[] = [];
    if (attCount) parts.push(t('email.attachments', { count: attCount }));
    if (threadCount) parts.push(t('email.threads', { count: threadCount }));
    if (msgCount) parts.push(t('email.mails', { count: msgCount }));
    return `${parts.join(', ')} ${t('email.selected')} · ${existingAttachmentCount + selected.size}/${CHAT_ATTACHMENT_MAX_COUNT}`;
  }, [existingAttachmentCount, selected]);

  /* ---- Filter labels ---- */
  const GMAIL_SYSTEM_LABELS = [
    { id: 'INBOX', name: t('email.inbox') },
    { id: 'SENT', name: t('email.sent') },
    { id: 'DRAFT', name: t('email.drafts') },
    { id: 'STARRED', name: t('email.starred') },
    { id: 'IMPORTANT', name: t('email.important') },
    { id: 'ALL', name: t('email.allMail') },
  ];

  const currentFilterName = useMemo(() => {
    if (activeTab === 'gmail') {
      const found = GMAIL_SYSTEM_LABELS.find((l) => l.id === activeLabel);
      if (found) return found.name;
      const custom = gmailLabels?.find((l) => l.id === activeLabel);
      return custom?.name || activeLabel;
    }
    const i18nKey = OUTLOOK_WELL_KNOWN_FOLDER_I18N[activeFolder.toLowerCase()];
    if (i18nKey) return t(i18nKey);
    const found = outlookFolders?.find((f) => f.id === activeFolder || f.name.toLowerCase() === activeFolder.toLowerCase());
    return found?.name || activeFolder;
  }, [activeTab, activeLabel, activeFolder, gmailLabels, outlookFolders, t]);

  /* ---- Render: not connected ---- */
  const renderNotConnected = () => {
    const isGmail = activeTab === 'gmail';
    return (
      <NotConnectedArea>
        <EmptyState
          icon={
            <MailLogoWrap>
              <img
                src={isGmail ? '/logo/google_gmail.webp' : '/logo/outlook.webp'}
                alt={isGmail ? 'Gmail' : 'Outlook'}
                width={48}
                height={48}
              />
            </MailLogoWrap>
          }
          title={isGmail ? t('email.gmailNotConnected') : t('email.outlookNotConnected')}
          description={t('email.connectFromPlugins')}
          action={
            <ConnectCtaButton
              variant="primary"
              size="md"
              onClick={handleGoToPlugin}
              $surfaceOpacity={surfaceOpacity}
            >
              {t('email.connect')}
            </ConnectCtaButton>
          }
        />
      </NotConnectedArea>
    );
  };

  /* ---- Render: skeleton ---- */
  const renderSkeleton = () => (
    <SkeletonArea>
      {Array.from({ length: 6 }).map((_, i) => (
        <SkeletonRow key={i}>
          <SkeletonLine width="100%" height="14px" />
          <SkeletonLine width="70%" height="12px" />
          <SkeletonLine width="90%" height="11px" />
        </SkeletonRow>
      ))}
    </SkeletonArea>
  );

  /* ---- Render: mail list ---- */
  const renderMailList = () => (
    <>
      <SearchFilterBar>
        <SearchWrap>
          <SearchInput
            variant="search"
            inputSize="sm"
            placeholder={t('email.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchQuery && (
            <ClearSearchBtn onClick={() => { setSearchQuery(''); setDebouncedSearchQuery(''); }}>
              <X size={14} />
            </ClearSearchBtn>
          )}
        </SearchWrap>
        <FilterWrap ref={filterRef}>
          <FilterBtn onClick={() => setShowFilterDropdown((v) => !v)}>
            <span>{currentFilterName}</span>
            <ChevronDown size={12} />
          </FilterBtn>
          {showFilterDropdown && (
            <FilterDropdown>
              {activeTab === 'gmail' ? (
                <>
                  {GMAIL_SYSTEM_LABELS.map((l) => (
                    <FilterOption
                      key={l.id}
                      $active={activeLabel === l.id}
                      onClick={() => { setActiveLabel(l.id); setShowFilterDropdown(false); }}
                    >
                      {l.name}
                    </FilterOption>
                  ))}
                  {gmailLabels?.filter((l) => l.type === 'user').map((l) => (
                    <FilterOption
                      key={l.id}
                      $active={activeLabel === l.id}
                      onClick={() => { setActiveLabel(l.id); setShowFilterDropdown(false); }}
                    >
                      {l.name}
                    </FilterOption>
                  ))}
                </>
              ) : (
                outlookFolders?.map((f) => (
                  <FilterOption
                    key={f.id}
                    $active={activeFolder === f.id}
                    onClick={() => { setActiveFolder(f.id); setShowFilterDropdown(false); }}
                  >
                    {f.name}
                    {f.unread_count > 0 && <UnreadBadge>{f.unread_count}</UnreadBadge>}
                  </FilterOption>
                ))
              )}
            </FilterDropdown>
          )}
        </FilterWrap>
      </SearchFilterBar>

      <MailScrollArea ref={scrollRef}>
        {loading && renderSkeleton()}
        {!loading && error && (
          <EmptyState
            icon={<RefreshCw size={32} />}
            title={t('email.errorOccurred')}
            description={error}
            action={
              <Button variant="secondary" size="sm" onClick={() => { void messagesQuery.refetch(); }}>
                {t('email.retry')}
              </Button>
            }
          />
        )}
        {!loading && !error && messages.length === 0 && (
          <EmptyState
            icon={<Inbox size={32} />}
            title={searchQuery ? t('email.noSearchResults') : t('email.noMails')}
            description={searchQuery ? t('email.tryOtherSearch') : ''}
          />
        )}
        {!loading && !error && messages.map((msg) => (
          <MailRow
            key={msg.id}
            $active={selectedMsg?.id === msg.id}
            $unread={!msg.is_read}
            onClick={() => handleMsgClick(msg)}
          >
            <MailRowHeader>
              <MailSender $unread={!msg.is_read}>{extractSenderName(msg.sender) || extractSenderEmail(msg.sender)}</MailSender>
              <MailDate>{formatDate(msg.date, t)}</MailDate>
            </MailRowHeader>
            <MailSubject $unread={!msg.is_read}>{msg.subject || t('email.noSubject')}</MailSubject>
            <MailSnippet>{msg.snippet}</MailSnippet>
            {msg.has_attachments && (
              <MailAttachIcon><Paperclip size={11} /></MailAttachIcon>
            )}
          </MailRow>
        ))}
        {loadingMore && (
          <LoadMoreIndicator>
            <Loader size={16} className="spin" />
            <span>{t('email.loading')}</span>
          </LoadMoreIndicator>
        )}
        <div ref={sentinelRef} style={{ height: 1 }} />
      </MailScrollArea>
    </>
  );

  /* ---- Render: mail detail ---- */
  const renderMailDetail = () => {
    if (!selectedMsg) {
      return (
        <DetailEmpty>
          <Mail size={40} strokeWidth={1} />
          <span>{t('email.selectMail')}</span>
        </DetailEmpty>
      );
    }

    if (loadingThread) {
      return (
        <DetailLoading><RingSpinner size={24} /></DetailLoading>
      );
    }

    const threadId = selectedMsg.thread_id || selectedMsg.conversation_id || selectedMsg.id;
    const threadKey = `thread:${threadId}`;
    const isThreadSelected = selected.has(threadKey);
    const hasReachedSelectionLimit = getRemainingAttachmentSlots(existingAttachmentCount, selected.size) === 0;
    const threadSelectionDisabled = !isThreadSelected && hasReachedSelectionLimit;

    return (
      <>
        <DetailHeader>
          <DetailBackBtn $visible={compactLayout} onClick={() => setSelectedMsg(null)}>
            <ArrowLeft size={16} />
          </DetailBackBtn>
          <DetailSubject>{selectedMsg.subject || t('email.noSubject')}</DetailSubject>
        </DetailHeader>

        {showUploadActions && threadMessages.length > 1 && (
          <ThreadActions>
            <ThreadImportBtn
              $active={isThreadSelected}
              $disabled={threadSelectionDisabled}
              onClick={() => toggleThreadImport(selectedMsg, activeTab)}
              title={threadSelectionDisabled ? t('email.maxAttachments', { max: CHAT_ATTACHMENT_MAX_COUNT }) : undefined}
            >
              <Checkbox
                checked={isThreadSelected}
                onChange={() => toggleThreadImport(selectedMsg, activeTab)}
                size="sm"
                disabled={threadSelectionDisabled}
              />
              <span>{t('email.fetchWholeThread')} ({threadMessages.length})</span>
            </ThreadImportBtn>
          </ThreadActions>
        )}

        <DetailScrollArea>
          {threadMessages.map((msg) => {
            const isExpanded = expandedThreadMsgs.has(msg.id);
            const msgKey = `msg:${msg.id}`;
            const isMsgSelected = selected.has(msgKey);
            const msgSelectionDisabled = !isMsgSelected && hasReachedSelectionLimit;

            return (
              <ThreadMessage key={msg.id}>
                <ThreadMsgHeader onClick={() => {
                  setExpandedThreadMsgs((prev) => {
                    const next = new Set(prev);
                    if (next.has(msg.id)) next.delete(msg.id);
                    else next.add(msg.id);
                    return next;
                  });
                }}>
                  <ThreadMsgSender>
                    <SenderAvatar>{(extractSenderName(msg.sender) || extractSenderEmail(msg.sender))[0]?.toUpperCase()}</SenderAvatar>
                    <SenderInfo>
                      <SenderName>{extractSenderName(msg.sender) || extractSenderEmail(msg.sender)}</SenderName>
                      <SenderDate>{formatDate(msg.date, t)}</SenderDate>
                    </SenderInfo>
                  </ThreadMsgSender>
                  <ThreadMsgActions>
                    {showUploadActions && (
                      <MsgImportBtn
                        $active={isMsgSelected}
                        $disabled={msgSelectionDisabled}
                        title={msgSelectionDisabled ? t('email.maxAttachments', { max: CHAT_ATTACHMENT_MAX_COUNT }) : t('email.fetchThisMail')}
                        onClick={(e) => { e.stopPropagation(); toggleMessageImport(msg, activeTab); }}
                      >
                        <Download size={13} />
                      </MsgImportBtn>
                    )}
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </ThreadMsgActions>
                </ThreadMsgHeader>

                {isExpanded && (
                  <ThreadMsgBody>
                    {(() => {
                      const bodyPayload = buildBodyRenderPayload(msg.body, msg.snippet);
                      if (bodyPayload.kind === 'empty') {
                        return <BodyPlaceholder>{t('email.noBody')}</BodyPlaceholder>;
                      }
                      if (bodyPayload.kind === 'html') {
                        return (
                          <HtmlBodyFrameShell>
                            <HtmlBodyFrame
                              title={t('email.originalBody')}
                              srcDoc={bodyPayload.srcDoc}
                              sandbox="allow-popups allow-popups-to-escape-sandbox"
                              referrerPolicy="no-referrer"
                              loading="lazy"
                            />
                          </HtmlBodyFrameShell>
                        );
                      }
                      if (bodyPayload.format === 'markdown') {
                        return (
                          <PlainBodyLayout>
                            {!!bodyPayload.main && (
                              <MarkdownBodyRenderer remarkPlugins={MARKDOWN_REMARK_PLUGINS}>
                                {bodyPayload.main}
                              </MarkdownBodyRenderer>
                            )}
                            {!!bodyPayload.forwarded && (
                              <ForwardedBodyCard>
                                <BodySectionLabel>{t('email.forwardedMail')}</BodySectionLabel>
                                <MarkdownBodyRenderer remarkPlugins={MARKDOWN_REMARK_PLUGINS}>
                                  {bodyPayload.forwarded}
                                </MarkdownBodyRenderer>
                              </ForwardedBodyCard>
                            )}
                            {!!bodyPayload.signature && (
                              <SignatureBodyCard>
                                <BodySectionLabel>{t('email.signature')}</BodySectionLabel>
                                <MarkdownBodyRenderer remarkPlugins={MARKDOWN_REMARK_PLUGINS}>
                                  {bodyPayload.signature}
                                </MarkdownBodyRenderer>
                              </SignatureBodyCard>
                            )}
                          </PlainBodyLayout>
                        );
                      }
                      return (
                        <PlainBodyLayout>
                          {!!bodyPayload.main && (
                            <PlainBodyBlock>{bodyPayload.main}</PlainBodyBlock>
                          )}
                          {!!bodyPayload.forwarded && (
                            <ForwardedBodyCard>
                              <BodySectionLabel>{t('email.forwardedMail')}</BodySectionLabel>
                              <PlainBodyBlock>{bodyPayload.forwarded}</PlainBodyBlock>
                            </ForwardedBodyCard>
                          )}
                          {!!bodyPayload.signature && (
                            <SignatureBodyCard>
                              <BodySectionLabel>{t('email.signature')}</BodySectionLabel>
                              <PlainBodyBlock>{bodyPayload.signature}</PlainBodyBlock>
                            </SignatureBodyCard>
                          )}
                        </PlainBodyLayout>
                      );
                    })()}

                    {msg.attachments && msg.attachments.length > 0 && (
                      <AttachmentList>
                        <AttachmentTitle><Paperclip size={13} /> {t('email.attachmentFiles')}</AttachmentTitle>
                        {msg.attachments.map((att) => {
                          const attKey = `att:${att.message_id}:${att.id}`;
                          const isSelected = selected.has(attKey);
                          const attachmentSizeSelectable = isAttachmentSizeAllowed(att.size);
                          const attachmentCountSelectable = isSelected || !hasReachedSelectionLimit;
                          const attachmentSelectable = attachmentSizeSelectable && attachmentCountSelectable;
                          let attachmentDisabledTitle: string | undefined;

                          if (!attachmentSizeSelectable) {
                            attachmentDisabledTitle = t('email.fileTooLarge');
                          } else if (!attachmentCountSelectable) {
                            attachmentDisabledTitle = t('email.maxAttachments', { max: CHAT_ATTACHMENT_MAX_COUNT });
                          }

                          return (
                            <AttachmentRow
                              key={att.id}
                              $selected={isSelected}
                              $interactive={showUploadActions}
                              $disabled={showUploadActions && !attachmentSelectable}
                              onClick={showUploadActions ? () => toggleAttachment(att, activeTab) : undefined}
                              title={attachmentDisabledTitle}
                            >
                              {showUploadActions && (
                                <Checkbox
                                  checked={isSelected}
                                  onChange={() => toggleAttachment(att, activeTab)}
                                  size="sm"
                                  disabled={!attachmentSelectable}
                                />
                              )}
                              <AttachmentIcon><FileIcon size={14} /></AttachmentIcon>
                              <AttachmentInfo>
                                <AttachmentName>{att.filename}</AttachmentName>
                                <AttachmentSize>{formatFileSize(att.size)}</AttachmentSize>
                              </AttachmentInfo>
                            </AttachmentRow>
                          );
                        })}
                      </AttachmentList>
                    )}
                  </ThreadMsgBody>
                )}
              </ThreadMessage>
            );
          })}
        </DetailScrollArea>
      </>
    );
  };

  const showDetailPanel = compactLayout && !!selectedMsg;

  /* ---- Main render ---- */
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('email.title')}
      titleIcon={<Mail size={18} />}
      titleSuffix={(
        <HeaderRefreshBtn
          type="button"
          title={t('email.refreshMail')}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            handleHeaderRefresh();
          }}
        >
          <RefreshCw
            size={14}
            className={messagesQuery.isRefetching || pluginsFetching ? 'spin' : undefined}
          />
        </HeaderRefreshBtn>
      )}
      width={`${Math.max(320, Math.round(modalDefaultWidth))}px`}
      resizable
      defaultHeight={Math.max(360, Math.round(modalDefaultHeight))}
      minWidth={modalMinWidth}
      minHeight={360}
      draggable={modalDraggable}
      pinnable={modalPinnable}
      defaultPinned={modalDefaultPinned}
      initialOffsetX={modalInitialOffsetX}
      initialOffsetY={modalInitialOffsetY}
      opacityAdjustable={modalOpacityAdjustable}
      defaultOpacity={modalDefaultOpacity}
      closeOnBackdrop={modalCloseOnBackdrop}
      dimBackdrop={modalDimBackdrop}
      passthroughBackdrop={modalPassthroughBackdrop}
      centerPaddingLeft={modalCenterPaddingLeft}
      centerPaddingRight={modalCenterPaddingRight}
      mountMode={mountMode}
      hostBounds={hostBounds}
      onSizeChange={modalOnSizeChange}
      onOffsetChange={modalOnOffsetChange}
      onPinnedChange={modalOnPinnedChange}
      onOpacityChange={modalOnOpacityChange}
      layerZIndex={modalLayerZIndex}
      onActivate={modalOnActivate}
    >
      <ModalBody
        ref={layoutRef}
        style={{ ['--email-ui-opacity' as '--email-ui-opacity']: String(surfaceOpacity) }}
      >
        <EmailTabs
          items={tabItems}
          activeId={activeTab}
          onChange={(id) => {
            const next = id as MailProvider;
            setActiveTab(next);
            onTabChange?.(next);
          }}
        />
        <TabContent>
          {pluginsLoading ? (
            <LoadingWrap><RingSpinner size={24} /></LoadingWrap>
          ) : !isConnected(activeTab) ? (
            renderNotConnected()
          ) : (
            <BrowserArea>
              <SplitPane $compact={compactLayout} $showDetail={showDetailPanel}>
                <MailListPane $compact={compactLayout}>
                  {renderMailList()}
                </MailListPane>
                <DetailPane $compact={compactLayout}>
                  {renderMailDetail()}
                </DetailPane>
              </SplitPane>
              {showUploadActions && (
                <ActionBar>
                  <SelectedCount>{selectionSummary}</SelectedCount>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={selected.size === 0}
                    onClick={handleAttach}
                  >
                    {t('email.attach')}
                  </Button>
                </ActionBar>
              )}
            </BrowserArea>
          )}
        </TabContent>
      </ModalBody>
    </Modal>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const HeaderRefreshBtn = styled.button`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  .spin {
    animation: header-spin 0.9s linear infinite;
  }

  @keyframes header-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  margin: 0 -24px -24px;

  * {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  *::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }
`;

const EmailTabs = styled(BaseTabs)`
  background: transparent;
`;

const TabContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const LoadingWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NotConnectedArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ConnectCtaButton = styled(Button)<{ $surfaceOpacity: number }>`
  && {
    border: 1px solid ${({ theme, $surfaceOpacity }) =>
      theme.mode === 'dark'
        ? `rgba(255,255,255,${Math.max(0.10, Math.min(0.18, $surfaceOpacity * 0.18))})`
        : `rgba(0,0,0,${Math.max(0.06, Math.min(0.12, $surfaceOpacity * 0.12))})`};
    background: ${({ theme, $surfaceOpacity }) =>
      theme.mode === 'dark'
        ? `rgba(255,255,255,${Math.max(0.05, Math.min(0.12, $surfaceOpacity * 0.12))})`
        : `rgba(255,255,255,${Math.max(0.72, Math.min(0.88, $surfaceOpacity * 0.88))})`};
    box-shadow: none;
    color: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.92)' : theme.colors.textPrimary)};
  }

  &&:hover:not(:disabled) {
    background: ${({ theme, $surfaceOpacity }) =>
      theme.mode === 'dark'
        ? `rgba(255,255,255,${Math.max(0.08, Math.min(0.16, $surfaceOpacity * 0.16))})`
        : `rgba(255,255,255,${Math.max(0.78, Math.min(0.94, $surfaceOpacity * 0.94))})`};
    border-color: ${({ theme, $surfaceOpacity }) =>
      theme.mode === 'dark'
        ? `rgba(255,255,255,${Math.max(0.14, Math.min(0.24, $surfaceOpacity * 0.24))})`
        : `rgba(0,0,0,${Math.max(0.08, Math.min(0.16, $surfaceOpacity * 0.16))})`};
    box-shadow: none;
  }
`;

const MailLogoWrap = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    object-fit: contain;
  }
`;

const TabIcon = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
`;

const BrowserArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const SplitPane = styled.div<{ $compact: boolean; $showDetail: boolean }>`
  flex: 1;
  display: flex;
  min-height: 0;
  ${({ $compact, $showDetail }) =>
    $compact &&
    css`
      width: 200%;
      transform: translateX(${$showDetail ? '-50%' : '0%'});
      transition: transform 240ms cubic-bezier(0.22, 1, 0.36, 1);
    `}
`;

/* ---- Mail list pane ---- */

const MailListPane = styled.div<{ $compact?: boolean }>`
  width: 320px;
  min-width: 280px;
  display: flex;
  flex-direction: column;
  border-right: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  ${({ $compact }) =>
    $compact &&
    css`
      width: 50%;
      min-width: 50%;
      max-width: 50%;
    `}
`;

const SearchFilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  flex-shrink: 0;
`;

const SearchWrap = styled.div`
  flex: 1;
  position: relative;
`;

const SearchInput = styled(Input)`
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.46)'};
  border-color: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.1)'};
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);

  &:hover {
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.16)'};
  }

  &:focus-within {
    border-color: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.26)' : 'rgba(0,0,0,0.2)'};
    box-shadow: ${({ theme }) =>
      theme.mode === 'dark'
        ? '0 0 0 2px rgba(255,255,255,0.08)'
        : '0 0 0 2px rgba(0,0,0,0.06)'};
  }
`;

const ClearSearchBtn = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.08)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const FilterWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const FilterBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 8px;
  font-size: 0.75rem;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const FilterDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  min-width: 160px;
  max-height: 240px;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
  border-radius: 12px;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.4)'
      : '0 8px 32px rgba(0,0,0,0.1)'};
  z-index: 10;
  padding: 4px;
`;

const FilterOption = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  font-size: 0.8125rem;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.textPrimary : theme.colors.textSecondary};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  background: ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark' ? 'rgba(255,149,0,0.12)' : 'rgba(255,149,0,0.1)'
      : 'transparent'};
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  }
`;

const UnreadBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 9px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(96,165,250,0.2)' : 'rgba(59,130,246,0.12)'};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#93bbfc' : '#2563eb'};
`;

const MailScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const MailRow = styled.div<{ $active: boolean; $unread: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 14px;
  cursor: pointer;
  border-bottom: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  position: relative;
  transition: background 0.2s ease;

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.mode === 'dark' ? 'rgba(255,149,0,0.12)' : 'rgba(255,149,0,0.1)'};
    `}

  ${({ $unread, theme }) =>
    $unread &&
    css`
      border-left: 3px solid ${theme.mode === 'dark' ? '#60a5fa' : '#3b82f6'};
    `}

  &:hover {
    background: ${({ $active, theme }) =>
      $active
        ? theme.mode === 'dark' ? 'rgba(255,149,0,0.15)' : 'rgba(255,149,0,0.12)'
        : theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'};
  }
`;

const MailRowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const MailSender = styled.span<{ $unread: boolean }>`
  font-size: 0.8125rem;
  font-weight: ${({ $unread }) => ($unread ? 600 : 400)};
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

const MailDate = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
`;

const MailSubject = styled.div<{ $unread: boolean }>`
  font-size: 0.75rem;
  font-weight: ${({ $unread }) => ($unread ? 600 : 400)};
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MailSnippet = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MailAttachIcon = styled.span`
  position: absolute;
  right: 14px;
  bottom: 10px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/* ---- Detail pane ---- */

const DetailPane = styled.div<{ $compact?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  ${({ $compact }) =>
    $compact &&
    css`
      width: 50%;
      min-width: 50%;
      max-width: 50%;
    `}
`;

const DetailEmpty = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.875rem;
`;

const DetailLoading = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-bottom: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  flex-shrink: 0;
`;

const DetailBackBtn = styled.button<{ $visible?: boolean }>`
  display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.textMuted};
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

`;

const DetailSubject = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

const ThreadActions = styled.div`
  padding: 8px 14px;
  border-bottom: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  flex-shrink: 0;
`;

const ThreadImportBtn = styled.div<{ $active: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 10px;
  font-size: 0.75rem;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark' ? 'rgba(96,165,250,0.12)' : 'rgba(59,130,246,0.08)'
      : theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $disabled, theme }) =>
      $disabled
        ? (theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)')
        : (theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)')};
  }
`;

const DetailScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

/* ---- Thread message ---- */

const ThreadMessage = styled.div`
  border-bottom: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
`;

const ThreadMsgHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'};
  }
`;

const ThreadMsgSender = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const SenderAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 22%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(145deg, #434345, #38383a)'
      : 'linear-gradient(145deg, #eaeaed, #e1e1e5)'};
  color: ${({ theme }) =>
    theme.mode === 'dark' ? '#c8c8cc' : '#4a4a4e'};
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 1px 3px rgba(0,0,0,0.35)'
      : '0 1px 3px rgba(0,0,0,0.08)'};
`;

const SenderInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const SenderName = styled.span`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SenderDate = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const ThreadMsgActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
`;

const MsgImportBtn = styled.button<{ $active: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  transition: all 0.2s ease;
  color: ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark' ? '#93bbfc' : '#2563eb'
      : theme.colors.textMuted};
  background: ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark' ? 'rgba(96,165,250,0.15)' : 'rgba(59,130,246,0.1)'
      : 'transparent'};

  &:hover {
    background: ${({ $disabled, theme }) =>
      $disabled
        ? 'transparent'
        : (theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)')};
    color: ${({ $disabled, theme }) =>
      $disabled ? theme.colors.textMuted : theme.colors.textPrimary};
  }
`;

const ThreadMsgBody = styled.div`
  padding: 0 14px 14px;
`;

const BodyPlaceholder = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-style: italic;
  padding: 8px 0;
`;

const HtmlBodyFrameShell = styled.div`
  max-height: 300px;
  border-radius: 10px;
  overflow: hidden;
  border: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
  background: #fff;
`;

const HtmlBodyFrame = styled.iframe`
  width: 100%;
  height: 300px;
  display: block;
  border: 0;
  background: #fff;
`;

const PlainBodyLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 0.8125rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textPrimary};
  max-height: 300px;
  overflow-y: auto;
`;

const MarkdownBodyRenderer = styled(ReactMarkdown)`
  font-size: 0.8125rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textPrimary};
  word-break: break-word;

  p, li, blockquote, td, th {
    white-space: pre-wrap;
  }

  p {
    margin: 0.5em 0;
  }

  p:first-child {
    margin-top: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul, ol {
    margin: 0.5em 0;
    padding-left: 20px;
  }

  a {
    color: ${({ theme }) => theme.mode === 'dark' ? '#93bbfc' : '#2563eb'};
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  pre {
    margin: 0.5em 0;
    white-space: pre-wrap;
  }
`;

const PlainBodyBlock = styled.pre`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
`;

const BodySectionLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 6px;
`;

const ForwardedBodyCard = styled.div`
  border-left: 3px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
  border-radius: 8px;
  padding: 10px 12px;
`;

const SignatureBodyCard = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  padding-top: 10px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/* ---- Attachments ---- */

const AttachmentList = styled.div`
  margin-top: 12px;
  border-radius: 12px;
  border: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  overflow: hidden;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)'};
`;

const AttachmentTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
  border-bottom: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
`;

const AttachmentRow = styled.div<{ $selected: boolean; $interactive?: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: ${({ $interactive, $disabled }) => ($interactive ? ($disabled ? 'not-allowed' : 'pointer') : 'default')};
  opacity: ${({ $disabled }) => ($disabled ? 0.65 : 1)};
  transition: background 0.2s ease;

  ${({ $selected, theme }) =>
    $selected &&
    css`
      background: ${theme.mode === 'dark' ? 'rgba(255,149,0,0.1)' : 'rgba(255,149,0,0.08)'};
    `}

  &:hover {
    background: ${({ $interactive, $disabled, theme }) =>
      $interactive
        ? ($disabled
          ? 'transparent'
          : theme.mode === 'dark'
            ? 'rgba(255,255,255,0.04)'
            : 'rgba(0,0,0,0.02)')
        : 'transparent'};
  }

  & + & {
    border-top: 0.5px solid ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  }
`;

const AttachmentIcon = styled.div`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const AttachmentInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const AttachmentName = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AttachmentSize = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/* ---- Action bar ---- */

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  flex-shrink: 0;
`;

const SelectedCount = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/* ---- Skeleton ---- */

const SkeletonArea = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px 0;
`;

const SkeletonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  border-bottom: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
`;

const LoadMoreIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 0;
  color: ${({ theme }) => theme.colors?.textSecondary ?? '#888'};
  font-size: 13px;

  .spin {
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
